/** Цикл создания поставок Ozon: спрашиваем площадку, сколько она возьмёт.
 *
 *  Отдельный файл, а не строки в `supply.ts`: раздел живёт в своей ветке параллельно с
 *  прод-фичами, и общий файл означал бы конфликт на каждом слиянии.
 */
import { readBlobError } from "@/lib/api-errors";
import { api } from "./client";

/** Файл или НАСТОЯЩАЯ причина отказа: у blob-запросов тело ошибки тоже блоб (см. readBlobError). */
async function blobOrReason(run: Promise<{ data: Blob }>): Promise<Blob> {
  try {
    return (await run).data;
  } catch (e) {
    throw await readBlobError(e);
  }
}

export interface OzonLine {
  article: string;
  nom_name: string;
  mp_sku: string;
  qty: number;
  accepted: number;
  dropped: number;
}

export interface OzonOnec {
  state: string; // order|pallets|files|transfer|invoice|done
  /** Номер заказа покупателя. Всегда есть: бэкенд пишет ссылку и номер только вместе и
   *  отказывает, если номер потерялся, — прочерк вместо номера тут показывать нечему. */
  order_number: string;
  pallets: string | null;
  transfer_number: string | null;
  invoice_number: string | null;
  /** Куда пишутся паллеты (`PALLETS_TARGET` бэкенда): `1c` — документы, номера в `pallets`;
   *  `portal` — план в портале склада (`getOzonPalletPlan`: SSCC, коробá). */
  pallets_target: "1c" | "portal";
  /** Последняя ревизия плана паллет, принятая порталом от нас; null — не писали. */
  pallet_plan_revision: number | null;
  /** Последний отказ портала «план держит склад»; null — нет. Показывается на поставке крупно. */
  pallet_plan_conflict: OzonPalletPlanConflict | null;
}

/** Отказ «план держит склад», записанный на поставку: каскад правки закончен, план паллет в
 *  портал НЕ записан, поверх склада seller-helper не пишет. */
export interface OzonPalletPlanConflict {
  /** stale_revision | order_checked | order_shipped */
  code: string | null;
  /** текст портала и почему поверх не пишем */
  message: string;
  our_revision: number | null;
  live_revision: number | null;
  live_source: string | null;
  at: string;
}

/** Паллета живого плана портала склада. */
export interface OzonPlanPallet {
  position: number | null;
  /** номер НАШЕГО места (по ключу паллеты); null — паллета не наша (склад завёл сам) */
  place: number | null;
  sscc: string | null;
  /** номер документа `rs_Паллеты` — только у паллет, импортированных из 1С */
  archive_number: string | null;
  unit_status: string | null;
  /** проверка заказа, которая её собирает; null — сборка не начиналась */
  check: number | null;
  /** коробов Ozon по плану и сколько из них собрано */
  boxes: number;
  matched: number;
}

/** План паллет поставки в портале склада (`PALLETS_TARGET=portal`). */
export interface OzonPalletPlan {
  target: "1c" | "portal";
  revision: number | null;
  /** последняя запись ушла без ответа — её дошлёт следующий шаг тем же телом */
  pending: boolean;
  live: {
    revision: number | null;
    /** seller_helper | layout («Раскладка» склада) | import_1c | bot */
    source: string | null;
    pallets: OzonPlanPallet[];
  } | null;
  /** живой план сменил не seller-helper — текст для человека; сами поверх мы не пишем */
  conflict: string | null;
  /** конфликт можно перезаписать нашим планом (`overwriteOzonPalletPlan`): чужая живая ревизия,
   *  а проверка заказа не завершена и паллеты не отгружены. Только тогда — красная кнопка. */
  overridable: boolean;
  /** последний отказ «план держит склад», записанный на поставку */
  recorded: OzonPalletPlanConflict | null;
}

/** Направление = наша поставка (юрлицо × регион) в разговоре с площадкой. */
/** Склад кластера так, как его видит менеджер при выборе. */
export interface OzonWarehouseOffer {
  warehouse_id: number;
  name: string;
  /** сюда можно везти. ⚠️ НЕ значит «возьмут всё» — сколько именно, говорит `accepted` */
  available: boolean;
  /** FULL_AVAILABLE | PARTIAL_AVAILABLE | NOT_AVAILABLE — слово площадки, не наше */
  status: string;
  /** отказ площадки «по-русски (КОД)»; незнакомый код — как есть */
  reason: string;
  /** объём взятого в литрах ПО ВЕРСИИ OZON (по габаритам её карточек — по нему она и отказывает
   *  точке за объём). null — площадка не отдала, это не ноль */
  volume_l: number | null;
  accepted: number;
  dropped: number;
  /** ЧТО именно отсечёт — только позиции с потерей, по убыванию. Без этого выбор склада
   *  слепой: «отсечёт 200» не отвечает на вопрос «а если убрать одну позицию и не ехать
   *  лишние 200 км». */
  cuts: { mp_sku: string; article: string; dropped: number }[];
}

/** Идущая правка состава, уже согласованного площадкой.
 *
 *  ⚠️ Не украшение: пока она есть, редактор раскладки разморожен (правится место с кодом),
 *  сверка состава показывает дельту вместо ошибки, а «Перевыпустить места», «Снять коды» и
 *  «Записать в 1С» откажут — экран обязан объяснить это ДО нажатия. */
export interface OzonChange {
  id: number;
  /** editing → content → cargo_delete → cargo_send → onec_unpost → onec_rows → onec_repost →
   *  files → done; aborted — откат */
  state: string;
  /** чем сорвался последний шаг; null — не срывался */
  error: string | null;
  created_by: string;
  created_at: string;
  /** версия бумаг поставки: вложения заказа 1С различаются суффиксом `-r<revision>` */
  revision: number;
  /** План, ПО КОТОРОМУ идёт применение, и его `notes` — протокол каскада. Пусто, пока заход в
   *  правке: там план считают заново каждым превью (`change/preview`).
   *
   *  ⚠️ Лежит в БД КАК БЫЛ ПОСЧИТАН — в том числе старой версией портала. Читать его местами
   *  (`places`) можно только через `savedPlan()`: он и есть единственная нормализация. */
  plan: OzonSavedChangePlan | null;
  /** Что перепечатать после этой правки — пара «какие места» и «печатали ли». */
  labels_to_reprint: OzonReprint;
}

/** Строка состава заявки, которая ИЗМЕНИЛАСЬ: столько площадка держит, столько поедет. */
export interface OzonContentRow {
  mp_sku: string;
  article: string;
  agreed: number;
  target: number;
}

/** Место в плане каскада: что у него снимать и сколько коробов досылать. */
export interface OzonPlacePlan {
  position: number;
  kind: string;
  /** коды грузомест (коробов) НА СНЯТИЕ у площадки. У опустевшего места это все его коды, у
   *  изменённого — только снятые правкой; пусто у новых мест — кодов им ещё не выдали */
  cargo_ids: string[];
  /** коды коробов, которых правка НЕ КОСНУЛАСЬ: они остаются у площадки, наклейки на них верны */
  kept: string[];
  /** код транспортного места (палеты) — его удаляет отдельная ручка площадки. У изменённого
   *  места пусто: паллета переживает смену состава, её код и наклейка те же (замер 18.09.2026) */
  transport_cargo_id: string | null;
  /** сколько физических коробов уедет; null — не посчитать (не заполнен квант) */
  boxes: number | null;
  /** сколько коробов надо ДОСЛАТЬ в это же место; null — не посчитать (не заполнен квант),
   *  и это проблема плана, а не ноль */
  boxes_new: number | null;
}

/** Четыре класса мест. Атом правки — КОРОБ, а не место: у места с кодами можно снять часть
 *  коробов и дослать новые, не трогая остальные (`changed`), — само место живёт со своим
 *  номером, паллетой и наклейкой. Целиком место уходит только в `emptied`. */
export interface OzonPlacesPlan {
  unchanged: number[];
  /** место с кодами, у которого правка тронула ЧАСТЬ коробов */
  changed: OzonPlacePlan[];
  emptied: OzonPlacePlan[];
  new: OzonPlacePlan[];
}

/** Что каскад перепишет в 1С. Номера документов не меняются — правка идёт НА МЕСТЕ. */
export interface OzonOnecPlan {
  docs: { kind: string; number: string }[];
  /** added | changed | removed */
  rows: Record<string, number>;
  /** added | changed | removed: у паллеты, которой правка сменила состав, документ правится
   *  НА МЕСТЕ — она не «убыла и прибыла» */
  pallets: Record<string, number>;
}

/** Что перепечатать: номера мест, где появятся НОВЫЕ коробы (новые места и изменённые с
 *  досылкой), и — если сменился набор палет — наклейки палет. У изменённого места клеить надо
 *  не всё место: уцелевшие коробы уже с наклейками. */
export interface OzonLabelsPlan {
  boxes: number[];
  pallets: boolean;
  /** кодов до правки не было — `boxes` это ПЕРВАЯ печать этикеток, а не перепечатка */
  first_print: boolean;
}

/** Что сделает каскад, если его применить сейчас. Ни одного вызова наружу при расчёте не было.
 *
 *  ⚠️ `problems` не пусто — это НЕ ошибка ручки: план приходит целиком (200), просто применять
 *  его нельзя (`mode` = `blocked`). Отказ кодом оставил бы менеджера с пустым экраном вместо
 *  разбора, чего именно не хватает. */
export interface OzonChangePlan {
  /** none — применять нечего · delta — часть кодов живёт · replace — сменятся ВСЕ коды ·
   *  blocked — есть проблемы */
  mode: "none" | "delta" | "replace" | "blocked";
  content: OzonContentRow[];
  /** полный состав для `content/update`; null — состав заявки не менялся */
  content_payload: { sku: string; quantity: number; quant: number }[] | null;
  places: OzonPlacesPlan;
  /** null — документов 1С по поставке нет, переписывать нечего */
  onec: OzonOnecPlan | null;
  labels: OzonLabelsPlan;
  /** почему каскад запускать нельзя */
  problems: string[];
  /** то, что человек обязан прочитать до нажатия, но что не мешает применению */
  notes: string[];
}

/** План, СОХРАНЁННЫЙ в заходе: он посчитан тогда, когда каскад начинали, и той версией портала.
 *  Отличие от свежего ровно одно — класса `changed` в нём может не быть. */
export type OzonSavedChangePlan = Omit<OzonChangePlan, "places"> & {
  places: Omit<OzonPlacesPlan, "changed"> & { changed?: OzonPlacePlan[] };
};

/** Сохранённый план захода как обычный план. ⚠️ ЕДИНСТВЕННОЕ место, где подставляется пустой
 *  `changed`: планы, посчитанные ДО 18.09.2026, этого класса не содержат — тогда место с кодами
 *  правилось только целиком, и «изменённых» не было по построению. Разложенный по коду `?? []`
 *  прятал бы уже настоящую поломку ответа — поле обязано быть у всего, что считает бэкенд
 *  сегодня. */
export function savedPlan(ch: OzonChange): OzonChangePlan | null {
  if (ch.plan === null) return null;
  const { places } = ch.plan;
  return { ...ch.plan, places: { ...places, changed: places.changed ?? [] } };
}

/** Открыть правку состава, уже согласованного площадкой.
 *
 *  Ничего необратимого: заход снимает снимки «как отправлено» и «как записано в 1С» и
 *  размораживает редактор. Наружу правка уходит отдельным шагом (превью → применить). */
export async function startOzonChange(shipmentId: number): Promise<OzonChange> {
  const { data } = await api.post<OzonChange>(
    `/supply/ozon/shipments/${shipmentId}/change/start`,
  );
  return data;
}

/** Шапка идущей правки: шаг, ошибка, сохранённый план. ⚠️ Нет захода — 404, а не пустой объект. */
export async function getOzonChange(shipmentId: number): Promise<OzonChange> {
  const { data } = await api.get<OzonChange>(
    `/supply/ozon/shipments/${shipmentId}/change`,
  );
  return data;
}

/** Отменить неприменённую правку. Отдаёт ВОССТАНОВЛЕННУЮ раскладку — её экран и перерисовывает. */
export async function abortOzonChange(shipmentId: number): Promise<OzonPlaces> {
  const { data } = await api.post<OzonPlaces>(
    `/supply/ozon/shipments/${shipmentId}/change/abort`,
  );
  return data;
}

/** Что сделает каскад, если его применить. Ни площадка, ни 1С при этом ничего не узнают.
 *
 *  ⚠️ Считается заново на каждый показ: план — функция от ТЕКУЩЕЙ раскладки, и показанный из
 *  кеша он рассказывал бы про позавчерашнюю правку. В 1С ходит читать цены. */
export async function previewOzonChange(
  shipmentId: number,
): Promise<OzonChangePlan> {
  const { data } = await api.post<OzonChangePlan>(
    `/supply/ozon/shipments/${shipmentId}/change/preview`,
  );
  return data;
}

/** Применить правку: состав заявки → снятие кодов опустевших мест → досылка новых → 1С → бумаги.
 *
 *  ⚠️ Необратимо со второго шага. Повторное нажатие — ПРОДОЛЖЕНИЕ с сорвавшегося шага, а не
 *  новый заход: каскад идёт по сохранённому плану и сверяет факт у площадки. */
export async function applyOzonChange(shipmentId: number): Promise<OzonChange> {
  const { data } = await api.post<OzonChange>(
    `/supply/ozon/shipments/${shipmentId}/change/apply`,
  );
  return data;
}

/** Добавить товар в раскладку — единственный путь, которым состав РАСТЁТ после заявки.
 *  `to_position` пусто — новым местом типа `kind`. */
export async function addOzonPlaceItem(
  shipmentId: number,
  body: {
    mp_sku: string;
    qty: number;
    kind?: string;
    to_position?: number | null;
  },
): Promise<OzonPlaces> {
  const { data } = await api.post<OzonPlaces>(
    `/supply/ozon/shipments/${shipmentId}/places/add`,
    body,
  );
  return data;
}

/** Убрать позицию или уменьшить количество — единственный путь, которым состав УМЕНЬШАЕТСЯ.
 *
 *  ⚠️ `qty` — СКОЛЬКО УБРАТЬ, а не сколько оставить; пусто — позицию из места целиком. У места с
 *  кодом остаток уезжает в НОВОЕ место, старое остаётся пустым со своим кодом до каскада. */
export async function removeOzonPlaceItem(
  shipmentId: number,
  body: { position: number; mp_sku: string; qty?: number | null },
): Promise<OzonPlaces> {
  const { data } = await api.post<OzonPlaces>(
    `/supply/ozon/shipments/${shipmentId}/places/remove`,
    body,
  );
  return data;
}

/** Заменить партию у товара в месте: партию выбирает менеджер, портал проверяет остаток и срок.
 *
 *  ⚠️ Партия названа ПУЛОМ — `batch_key` ПЛЮС `char_key`: один ключ партии штатно лежит в
 *  остатке 1С под несколькими характеристиками (у готовой продукции характеристика — это канал:
 *  OZON, WB, пустая), и по одному ключу портал выбрал бы первый попавшийся пул. Обе части берём
 *  у той опции, которую человек выбрал глазами. */
export async function rebatchOzonPlaceItem(
  shipmentId: number,
  body: {
    position: number;
    mp_sku: string;
    batch_key: string;
    char_key: string;
  },
): Promise<OzonPlaces> {
  const { data } = await api.post<OzonPlaces>(
    `/supply/ozon/shipments/${shipmentId}/places/rebatch`,
    body,
  );
  return data;
}

/** Партия на выбор: пул, номер, срок и сколько её СВОБОДНО (с учётом чужих поставок и нашей
 *  раскладки).
 *
 *  Пул — `batch_key` вместе с `char_key`: две опции с ОДНИМ номером партии не дубли, а разные
 *  каналы остатка. `channel` — тот же пул словами («OZON»; у пустой характеристики пусто). */
export interface OzonBatchOption {
  batch_key: string;
  char_key: string;
  channel: string;
  batch_no: string;
  batch_exp: string;
  free_qty: number;
}

/** Годные партии этого товара в порядке FEFO. `position` — место, чьи строки партию сейчас
 *  держат: их остаток занятым не считается, иначе замена на ту же партию отказывала бы сама себе. */
export async function getOzonChangeBatches(
  shipmentId: number,
  mpSku: string,
  position: number,
): Promise<OzonBatchOption[]> {
  const { data } = await api.get<OzonBatchOption[]>(
    `/supply/ozon/shipments/${shipmentId}/change/batches`,
    { params: { mp_sku: mpSku, position } },
  );
  return data;
}

/** Карточка товара для «добавить артикул»: чем он зовётся у нас и у площадки. */
export interface OzonCatalogItem {
  mp_sku: string;
  article: string;
  name: string;
  /** квант заводского короба; null — в спеке 1С его нет, и место не посчитать */
  in_box: number | null;
}

/** Что можно добавить в поставку — из живой 1С, тем же путём, что и само добавление. */
export async function getOzonChangeCatalog(
  shipmentId: number,
  search: string,
): Promise<OzonCatalogItem[]> {
  const { data } = await api.get<OzonCatalogItem[]>(
    `/supply/ozon/shipments/${shipmentId}/change/catalog`,
    { params: { search } },
  );
  return data;
}

/** Этикетки ТОЛЬКО затронутых правкой мест: у уцелевших наклейки давно на коробах и верны.
 *
 *  ⚠️ Номера берёт бэкенд из СОХРАНЁННОГО плана последнего захода, а успешная печать проставляет
 *  «напечатано» — после неё напоминание о наклейках с экрана уходит. */
export async function getOzonChangedLabels(shipmentId: number): Promise<Blob> {
  return blobOrReason(
    api.post<Blob>(`/supply/ozon/shipments/${shipmentId}/places/labels`, null, {
      params: { changed_only: 1 },
      responseType: "blob",
    }),
  );
}

/** Что осталось наклеить после правки состава: какие места и печатали ли их уже.
 *
 *  Пара, а не два поля россыпью: `positions` без `printed_at` означал бы «перепечатать» вечно. */
export interface OzonReprint {
  /** номера мест, где появились новые коробы; пусто — перепечатывать нечего */
  positions: number[];
  /** сколько ЭТИКЕТОК в стопке — по числу новых кодов. ⚠️ Не число мест: у изменённого места
   *  клеят на новые коробы, а не на всю паллету */
  codes: number;
  /** когда печатали затронутые этикетки; null — ещё ни разу */
  printed_at: string | null;
}

export interface OzonDirection {
  shipment_id: number;
  region: string;
  legal_name: string;
  /** "" — не спрашивали | drafted — черновик снят | ordered — заявка | refused — не везут | failed */
  state: string;
  /** direct | crossdock. Правило компании: прямые в Москву и область, остальное кросс-доком. */
  kind: string;
  /** тип поставки: `true` — с грузоместами (палеты как транспортные места), `false` — обычная.
   *  Меняется, пока не выдан ни один код: дальше площадка переключатель блокирует. */
  transport: boolean;
  /** склады кластера с числами площадки — из них и выбирают (у кросс-дока пусто) */
  offers: OzonWarehouseOffer[];
  /** ВЫБРАННЫЙ склад; пусто — умолчание «кто возьмёт больше» */
  warehouse_pick: number | null;
  /** пункт СДАЧИ кросс-дока (туда везём мы), а не склад хранения (туда везёт площадка) */
  /** выбранный пункт сдачи; пусто — «повезём как обычно» */
  drop_off_id: number | null;
  /** имя выбранного пункта; null у выбранных до 12.10.2026 — их называет список `/drop-offs` по id.
   *  ПВЗ в том списке нет, поэтому имя берётся отсюда */
  drop_off_name: string | null;
  /** тип выбранного пункта в форме черновика: `CROSS_DOCK` у точек списка, `DELIVERY_POINT` у ПВЗ… */
  drop_off_type: string | null;
  /** склад, назначенный площадкой (у кросс-дока пусто — его назначает Ozon) */
  warehouse: string | null;
  warehouse_id: number | null;
  /** номер поставки — тот, что вписывается в 1С */
  supply_number: string | null;
  slot_from: string | null;
  slot_to: string | null;
  draft_id: string | null;
  drafted_at: string | null;
  order_id: string | null;
  /** Что записано в учёт; null — ничего.
   *
   *  ⚠️ Не справка: товар уходит из свободного остатка (и из следующего распределения) ровно в
   *  момент проведения ПТМО — она переводит ГП на юрлицо, а юрлиц-получателей в снимке остатков
   *  нет. Заказ не считается, он на получателе. */
  onec: OzonOnec | null;
  /** Поломка цикла 1С этой поставки — текст того же отказа, которым 409 отвечают её собственные
   *  ручки. Приходит только в СПИСКЕ: раньше одна такая поставка роняла весь список направлений,
   *  и «Сбросить цикл 1С» — путь починки — становился недоступен вместе со всем экраном.
   *  Не пусто — `onec` пуст не потому, что в учёте ничего нет, а потому, что показывать нечего. */
  broken?: string | null;
  /** Идущая правка состава; null — правки нет, экран работает как раньше. */
  change: OzonChange | null;
  /** Этикетки, которые ждут печати ПОСЛЕ применённой правки: заход закончился, каскад завёл новые
   *  места, а наклеек на их короба ещё нет (`POST …/places/labels?changed_only=1`). null — клеить
   *  нечего: правок не было, новых мест они не завели или стопку уже напечатали.
   *
   *  ⚠️ Отдельно от `change`: тот означает «идёт изменение», и напоминание о наклейках, прилетев
   *  в нём, держало бы поставку в стадии, из которой она давно вышла. */
  reprint: OzonReprint | null;
  /** Грузоместа: сколько непустых мест в раскладке и сколько уже стоит на площадке.
   *  Равенство и означает «состав подтверждён» — только по нему пишутся документы 1С. */
  places_total: number;
  places_sent: number;
  requested: number;
  accepted: number;
  dropped: number;
  note: string | null;
  lines: OzonLine[];
}

/** То же направление, но полученное по своему id — для страницы поставки.
 *
 *  Своё поле ровно одно: `run_id`. Страница открывается прямой ссылкой, без списка, а фиксация
 *  нужна и на «назад к поставкам Ozon», и за квантом заводского короба (он считается по всей
 *  фиксации разом). */
export interface OzonShipment extends OzonDirection {
  run_id: number;
}

/** Одна поставка Ozon по своему id. Вид ТОТ ЖЕ, что у строки списка: два экрана одной поставки
 *  не должны показывать разные числа. */
export async function getOzonShipment(
  shipmentId: number,
): Promise<OzonShipment> {
  const { data } = await api.get<OzonShipment>(
    `/supply/ozon/shipments/${shipmentId}`,
  );
  return data;
}

/* ────────────────────────── реестр поставок ────────────────────────── */

/** Группа статуса поставки — считает ОДНА функция бэкенда (`ozon_registry.group_of`).
 *
 *  `prep` — заявки нет; `work` — заявка есть, статус площадки не обновляли или DATA_FILLING;
 *  `transit`/`acceptance` — по коду площадки; `done`/`cancelled` — всегда в архиве; `unknown` —
 *  код, которого бэкенд не знает: его НЕ угадывают, а показывают как есть. */
export type OzonGroup =
  | "prep"
  | "work"
  | "transit"
  | "acceptance"
  | "done"
  | "cancelled"
  | "unknown";

/** Документ 1С поставки: заказ покупателя, ПТМО, счёт-фактура. */
export type OnecDocKind = "order" | "transfer" | "invoice";

/** Состояние одного документа в 1С на момент последнего обновления кнопкой.
 *
 *  `found=false` — по ссылке документа в 1С нет, остальные поля тогда null. `state` (название
 *  состояния из справочника) и `closed` — только у заказа, у ПТМО/СФ всегда null. `closed=null` —
 *  1С признак «закрыт» не отдала: по нему ничего не решаем. */
export interface OnecDoc {
  number: string;
  found: boolean;
  posted: boolean | null;
  deleted: boolean | null;
  state: string | null;
  closed: boolean | null;
}

/** Снимок цепочки 1С. Ключ null — документа на поставке нет (ссылки нет), а не «не нашли». */
export interface OnecDocs {
  order: OnecDoc | null;
  transfer: OnecDoc | null;
  invoice: OnecDoc | null;
}

/** Строка реестра: направление + фиксация, статус заявки у площадки и архив. */
export interface OzonRegistryRow extends OzonDirection {
  run_id: number;
  run_created_at: string;
  run_created_by: string;
  /** код `state` заявки из `/v3/supply-order/get`; null — кнопкой ещё не обновляли */
  ozon_order_state: string | null;
  ozon_order_state_at: string | null;
  group: OzonGroup;
  /** ручной архив ИЛИ группа done/cancelled */
  archived: boolean;
  /** в архив убрали руками — только такую можно «Вернуть» */
  archived_manually: boolean;
  /** снимок документов 1С; null — кнопкой у 1С ещё не спрашивали */
  onec_docs: OnecDocs | null;
  onec_docs_at: string | null;
}

/** Что изменила кнопка «Обновить статусы» у одной поставки (статус Ozon). */
export interface RefreshChange {
  shipment_id: number;
  region: string;
  legal_name: string;
  /** Номер поставки Ozon; null — ещё не присвоен. */
  supply_number: string | null;
  before: string | null;
  after: string;
}

/** Что изменила кнопка у одного документа 1С. `before`/`after` — резюме словами от бэкенда
 *  («В работе, проведён», «помечен на удаление», «не найден в 1С»); `before=null` — раньше
 *  1С не спрашивали. */
export interface OnecChange {
  shipment_id: number;
  region: string;
  legal_name: string;
  supply_number: string | null;
  doc: OnecDocKind;
  number: string;
  before: string | null;
  after: string;
}

/** Итог обновления статусов (Ozon + 1С). ⚠️ `errors` — не украшение: отказ кабинета, заявка,
 *  которой нет в ответе площадки, и недоступная 1С приходят сюда, а не роняют весь вызов. */
export interface RefreshResult {
  checked: number;
  changed: RefreshChange[];
  onec_checked: number;
  onec_changed: OnecChange[];
  errors: string[];
  refreshed_at: string;
}

/** Реестр по ВСЕМ фиксациям: `archived=false` — живые, `true` — архив. */
export async function getOzonRegistry(
  archived: boolean,
): Promise<OzonRegistryRow[]> {
  const { data } = await api.get<OzonRegistryRow[]>("/supply/ozon/registry", {
    params: { archived },
  });
  return data;
}

/** Спросить у площадки статусы заявок и у 1С — состояние документов. Только по кнопке — само по
 *  себе ничего не меняется. */
export async function refreshOzonStatuses(): Promise<RefreshResult> {
  const { data } = await api.post<RefreshResult>(
    "/supply/ozon/registry/refresh",
  );
  return data;
}

/** Убрать в архив поставку без заявки. С заявкой (или `ordering`) — 409 словами. */
export async function archiveOzonShipment(
  shipmentId: number,
): Promise<OzonRegistryRow> {
  const { data } = await api.post<OzonRegistryRow>(
    `/supply/ozon/shipments/${shipmentId}/archive`,
  );
  return data;
}

/** Вернуть из ручного архива. */
export async function unarchiveOzonShipment(
  shipmentId: number,
): Promise<OzonRegistryRow> {
  const { data } = await api.post<OzonRegistryRow>(
    `/supply/ozon/shipments/${shipmentId}/unarchive`,
  );
  return data;
}

/** Снять черновик по ОДНОМУ направлению. Обратимо: в кабинете не виден, живёт 30 минут.
 *  ⚠️ Площадка ограничивает черновики — 2 в минуту, поэтому и по одному.
 *
 *  Тип поставки НЕ передаём: он свойство направления, а не вызова — прямые в Москву и область,
 *  остальное кросс-доком, и решает это бэкенд. Пока клиент слал `kind=direct` «по умолчанию»,
 *  правило не работало вовсе: всё уезжало прямой поставкой, а экран рисовал «кросс-док». */
export async function draftOzonDirection(
  shipmentId: number,
): Promise<OzonDirection> {
  const { data } = await api.post<OzonDirection>(
    `/supply/ozon/shipments/${shipmentId}/draft`,
  );
  return data;
}

/** Квант заводского короба по каждому SKU фиксации: `{mp_sku: in_box}`.
 *
 *  Отдельным запросом, а не полем строки направления: квант живёт в живой 1С, и поход туда на
 *  каждый показ списка гасил бы весь экран, когда учёт недоступен. SKU без кванта в ответе нет —
 *  рисуем «—», а не «0 коробов». */
export async function getOzonBoxQuants(
  runId: number,
): Promise<Record<string, number>> {
  const { data } = await api.get<Record<string, number>>(
    `/supply/ozon/runs/${runId}/box-quants`,
  );
  return data;
}

/** Поправить количества состава (`{mp_sku: штук}`, только изменённые) и переспросить площадку.
 *
 *  Ответ площадки «возьму 40» — её ПОТОЛОК, а не наше обязательство: увезти можно меньше, хоть
 *  один заводской короб, и спросить больше тоже можно.
 *
 *  ⚠️ Тратит черновик (их 2 в минуту): иначе «сколько везём» разошлось бы с «сколько площадка
 *  согласилась взять», а раскладка считается именно по второму. */
export async function setOzonContent(
  shipmentId: number,
  qty: Record<string, number>,
): Promise<OzonDirection> {
  const { data } = await api.post<OzonDirection>(
    `/supply/ozon/shipments/${shipmentId}/content`,
    {
      qty,
    },
  );
  return data;
}

/** Окно приёмки как его назвала площадка. Отправляем обратно тем же текстом: своя нормализация
 *  времени — лишний повод разойтись с её часовым поясом. */
export interface OzonTimeslot {
  start: string;
  end: string;
}

/** Окна приёмки по снятому черновику — все, что даёт площадка. */
export async function getOzonTimeslots(
  shipmentId: number,
): Promise<OzonTimeslot[]> {
  const { data } = await api.get<OzonTimeslot[]>(
    `/supply/ozon/shipments/${shipmentId}/timeslots`,
  );
  return data;
}

/** Создать заявку. В кабинете она ВИДНА (в отличие от черновика), но отменяема. */
export async function createOzonOrder(
  shipmentId: number,
  slot: OzonTimeslot,
): Promise<OzonDirection> {
  const { data } = await api.post<OzonDirection>(
    `/supply/ozon/shipments/${shipmentId}/order`,
    slot,
  );
  return data;
}

/** Сверить с площадкой заявку, ответа по которой не было («заявка ушла, ответа нет»).
 *
 *  ⚠️ Не «перечитать экран»: ответ площадки сам не придёт, и состояние направления меняет
 *  только эта сверка. Ничего не создаёт — либо привязывает НАЙДЕННУЮ заявку, либо возвращает
 *  направление к «черновик снят», либо оставляет как есть и пишет в `note`, почему.
 */
export async function resolveOzonOrdering(
  shipmentId: number,
): Promise<OzonDirection> {
  const { data } = await api.post<OzonDirection>(
    `/supply/ozon/shipments/${shipmentId}/resolve-ordering`,
  );
  return data;
}

export async function cancelOzonOrder(
  shipmentId: number,
): Promise<OzonDirection> {
  const { data } = await api.post<OzonDirection>(
    `/supply/ozon/shipments/${shipmentId}/cancel`,
  );
  return data;
}

/** Сохранённая раскладка: её можно править до отправки на площадку. */
export interface SavedItem {
  mp_sku: string;
  article: string;
  name: string;
  qty: number;
  /** коробов под это количество; null — квант упаковки неизвестен, и «0» тут было бы враньём */
  boxes: number | null;
  /** квант заводского короба; 0 — неизвестен. По нему видно НЕПОЛНЫЙ короб */
  in_box: number;
  /** номер партии 1С — по нему кладовщик находит коробку */
  batch_no: string | null;
  /** чем закрывается ЭТА строка; строк у товара столько, сколько партий его закрывают */
  batch_exp: string | null;
  /** ключ партии — им, а не номером, различают «та же партия или нет»: номер 1С не уникален
   *  между номенклатурами. */
  batch_key: string | null;
  /** характеристика ПУЛА, из которого партия подобрана (канал продаж у ГП). Партия — это пул
   *  ЦЕЛИКОМ (`batch_key` + `char_key`): один ключ штатно лежит в остатке под несколькими
   *  характеристиками. */
  char_key: string | null;
  /** характеристика (канал продаж) пула словами — для человека, не путать с `char_key` (ключ).
   *  `null` — партия не подобрана либо подобрана до учёта характеристики; `""` — пустая
   *  характеристика («все МП»); иначе — название канала («OZON», «WB», …). */
  channel: string | null;
  /** коды ОТПРАВЛЕННЫХ коробок этой строки — по одному на физический короб. Суммой их показывать
   *  нельзя: код у каждой коробки свой, по нему её ищут и по нему печатается этикетка. */
  cargo_ids: string[];
}

export interface SavedPlace {
  position: number;
  kind: string; // PALLET | BOX
  qty: number;
  /** коробов в месте; null — хотя бы у одной строки квант неизвестен */
  boxes: number | null;
  /** код площадки; появился — состав заморожен, коды выдаются один раз */
  cargo_id: string | null;
  /** код ТРАНСПОРТНОГО места (палеты): грузоместами едут коробки, палета их объединяет */
  transport_cargo_id: string | null;
  items: SavedItem[];
}

export interface OzonPlaces {
  places: SavedPlace[];
  problems: string[];
  notes: string[];
  /** Лимиты площадки по заявке. Пусто — заявки ещё нет, они появляются вместе с ней.
   *  Считает площадка КОРОБКИ: грузоместо у неё — коробка, паллета их объединяет транспортным
   *  местом (`max_transport_pallet_count`). `max_pallet_count` — про палету-микс, которую портал
   *  не отправляет вовсе: её приёмка платная. */
  limits: Partial<{
    max_box_count: number;
    max_pallet_count: number;
    max_box_sku_count: number;
    max_transport_pallet_count: number;
  }>;
}

export async function getOzonPlaces(shipmentId: number): Promise<OzonPlaces> {
  const { data } = await api.get<OzonPlaces>(
    `/supply/ozon/shipments/${shipmentId}/places`,
  );
  return data;
}

/** Разложить автоматически и СОХРАНИТЬ — затирает ручные правки, поэтому только по явной команде. */
export async function computeOzonPlaces(
  shipmentId: number,
  strategy?: string,
): Promise<OzonPlaces> {
  const { data } = await api.post<OzonPlaces>(
    `/supply/ozon/shipments/${shipmentId}/places/compute`,
    null,
    { params: strategy ? { strategy } : undefined },
  );
  return data;
}

/** «Разложить заново» ВНУТРИ режима изменений: пересобрать раскладку по ТЕКУЩЕМУ составу.
 *
 *  Отдельная ручка от `compute`: там раскладывается согласованное площадкой (`ozon_accepted`),
 *  здесь — то, что менеджер уже наубирал и надобавлял. Места с кодами опустошаются и ждут снятия
 *  каскадом — коды сменятся у ВСЕХ мест.
 */
export async function recomputeOzonPlaces(
  shipmentId: number,
  strategy?: string,
): Promise<OzonPlaces> {
  const { data } = await api.post<OzonPlaces>(
    `/supply/ozon/shipments/${shipmentId}/places/recompute`,
    null,
    { params: strategy ? { strategy } : undefined },
  );
  return data;
}

/** «Перераспределить партии»: сверить раскладку с живым остатком 1С и переподобрать НЕХВАТКУ.
 *
 *  Годные строки не трогаются вовсе — места с кодами остаются нетронутыми вместе с этикетками.
 *  Сводка «переподобрано N строк в M местах» приезжает строкой в `notes`.
 */
export async function repickOzonPlaces(
  shipmentId: number,
): Promise<OzonPlaces> {
  const { data } = await api.post<OzonPlaces>(
    `/supply/ozon/shipments/${shipmentId}/places/repick`,
  );
  return data;
}

export async function moveOzonItems(
  shipmentId: number,
  body: {
    sku: string;
    qty: number;
    from_position: number;
    to_position: number | null;
    to_kind?: string;
  },
): Promise<OzonPlaces> {
  const { data } = await api.post<OzonPlaces>(
    `/supply/ozon/shipments/${shipmentId}/places/move`,
    body,
  );
  return data;
}

/** Что выбрано в раскладке: место целиком (`sku` пуст) или один артикул внутри него.
 *
 *  Одна форма выбора на все действия — и на сборку, и на разбор. Форм было две (чекбокс по местам
 *  и кнопка «перенести» по товарам), и товар внутри паллеты выбрать было нельзя вовсе. */
export interface OzonPick {
  position: number;
  sku?: string;
}

/** Собрать выбранное в ОДНО место: `to_position` пусто — в новую паллету, иначе в это место
 *  (тип у него свой и не меняется). */
export async function gatherOzonPlaces(
  shipmentId: number,
  body: { picks: OzonPick[]; to_position?: number | null },
): Promise<OzonPlaces> {
  const { data } = await api.post<OzonPlaces>(
    `/supply/ozon/shipments/${shipmentId}/places/gather`,
    body,
  );
  return data;
}

/** Разобрать выбранное на заводские короба — каждую позицию своими коробами. Ничего не сливает:
 *  два неполных короба одного товара так и останутся двумя. */
export async function ozonPlacesToBoxes(
  shipmentId: number,
  picks: OzonPick[],
): Promise<OzonPlaces> {
  const { data } = await api.post<OzonPlaces>(
    `/supply/ozon/shipments/${shipmentId}/places/boxes`,
    { picks },
  );
  return data;
}

/** Правило чек-листа площадки по заявке: она знает про свои требования больше, чем мы. */
export interface OzonRule {
  rule: string;
  name: string;
  ok: boolean;
  /** площадка сама делит правила на обязательные и нет — необязательные не красим как ошибку */
  required: boolean;
  detail: string;
}

/** Отправить грузоместа в заявку. ⚠️ Граница обратимости: коды выдаются один раз, по ним печатают
 *  этикетки. Повторный вызов ДОСЫЛАЕТ места без кодов, а не перевыпускает стоящие. */
export async function sendOzonPlaces(shipmentId: number): Promise<OzonPlaces> {
  const { data } = await api.post<OzonPlaces>(
    `/supply/ozon/shipments/${shipmentId}/places/send`,
  );
  return data;
}

/** Чек-лист площадки: её ответ на «всё ли заполнено». */
export async function getOzonChecklist(
  shipmentId: number,
): Promise<OzonRule[]> {
  const { data } = await api.get<OzonRule[]>(
    `/supply/ozon/shipments/${shipmentId}/places/checklist`,
  );
  return data;
}

/** Этикетки площадки С допечаткой артикула, партии, срока и номера места — файлом.
 *  Голый файл площадки идёт в её порядке страниц и состава не несёт: клеить по нему нельзя. */
export async function getOzonLabels(shipmentId: number): Promise<Blob> {
  return blobOrReason(
    api.post<Blob>(`/supply/ozon/shipments/${shipmentId}/places/labels`, null, {
      responseType: "blob",
    }),
  );
}

/** Выбрать склад прямой поставки из предложенных площадкой. `null` — вернуть умолчание.
 *  Нового черновика не тратит: числа по всем складам кластера площадка отдала разом. */
export async function setOzonWarehouse(
  shipmentId: number,
  warehouseId: number | null,
): Promise<OzonDirection> {
  const { data } = await api.post<OzonDirection>(
    `/supply/ozon/shipments/${shipmentId}/warehouse`,
    { warehouse_id: warehouseId },
  );
  return data;
}

/** Тип поставки: с грузоместами (палеты как транспортные места) или обычная.
 *  Меняет правила раскладки, поэтому сбрасывает места; после выдачи кодов недоступно. */
export async function setOzonTransport(
  shipmentId: number,
  on: boolean,
): Promise<OzonDirection> {
  const { data } = await api.post<OzonDirection>(
    `/supply/ozon/shipments/${shipmentId}/transport`,
    { on },
  );
  return data;
}

/** Наклейки ПАЛЕТ с нашей подписью «ПАЛЛЕТА №N» и числом коробов на ней.
 *  Отдельная ручка площадки: коробочная этикеточная транспортный код не признаёт вовсе. */
export async function getOzonPalletLabels(shipmentId: number): Promise<Blob> {
  return blobOrReason(
    api.post<Blob>(
      `/supply/ozon/shipments/${shipmentId}/places/pallet-labels`,
      null,
      {
        responseType: "blob",
      },
    ),
  );
}

/** Сборочный лист: «место ↔ код площадки ↔ товар и партия». */
export async function getOzonPackingList(shipmentId: number): Promise<Blob> {
  return blobOrReason(
    api.get<Blob>(`/supply/ozon/shipments/${shipmentId}/places/packing-list`, {
      responseType: "blob",
    }),
  );
}

/** Тот же сборочный лист таблицей — чтобы свести его с чужой выгрузкой в Excel.
 *  Состав берётся тем же путём, что и у PDF: две выгрузки одной поставки разъезжаться не должны. */
export async function getOzonPackingListXlsx(
  shipmentId: number,
): Promise<Blob> {
  return blobOrReason(
    api.get<Blob>(
      `/supply/ozon/shipments/${shipmentId}/places/packing-list.xlsx`,
      {
        responseType: "blob",
      },
    ),
  );
}

/** Удалить грузоместа на площадке и вернуть места в правку. */
export async function dropOzonCargoes(
  shipmentId: number,
  positions: number[],
): Promise<OzonPlaces> {
  const { data } = await api.post<OzonPlaces>(
    `/supply/ozon/shipments/${shipmentId}/places/drop`,
    { positions },
  );
  return data;
}

/** План паллет поставки в портале склада — только чтение. Портал не ответил — ошибка, а не
 *  «плана нет». */
export async function getOzonPalletPlan(
  shipmentId: number,
): Promise<OzonPalletPlan> {
  const { data } = await api.get<OzonPalletPlan>(
    `/supply/ozon/shipments/${shipmentId}/pallet-plan`,
  );
  return data;
}

/** Перезаписать чужую живую ревизию плана паллет (склад переложил «Раскладкой») нашим планом —
 *  поверх ЖИВОЙ ревизии. Только явным действием человека после согласования со складом (решение
 *  владельца §7.9, 27.10.2026). Отдаёт план заново. */
export async function overwriteOzonPalletPlan(
  shipmentId: number,
): Promise<OzonPalletPlan> {
  const { data } = await api.post<OzonPalletPlan>(
    `/supply/ozon/shipments/${shipmentId}/pallet-plan/overwrite`,
  );
  return data;
}

/** Забыть документы 1С по поставке, чтобы записать её заново.
 *
 *  ⚠️ Только после того, как документы ПОМЕЧЕНЫ НА УДАЛЕНИЕ в самой 1С: портал это проверяет и
 *  отказывает, пока хоть один жив. Нужен там, где состав переделали после записи в учёт — заказ
 *  резервирует конкретные партии и повторным прогоном не переписывается. */
export async function resetOnecCycle(
  shipmentId: number,
): Promise<OzonOnec | null> {
  // ⚠️ `null` — нормальный ответ: цикл забыт, показывать в учёте больше нечего
  const { data } = await api.post<OzonOnec | null>(
    `/supply/ozon/shipments/${shipmentId}/onec/reset`,
  );
  return data;
}

/** Перевыпустить грузоместа: пересобрать партии и отправить состав ЗАНОВО, с заменой.
 *
 *  ⚠️ Для случая «партия кончилась уже после отправки»: вместе с партией меняется срок годности
 *  внутри коробки, а он уже уехал на площадку. Поштучно все места площадка удалить не даёт, и
 *  состав уходит одним набором с заменой. КОДЫ МЕНЯЮТСЯ У ВСЕХ мест — этикетки надо печатать
 *  заново. После проведённой ПТМО ручка отказывает: товар уже переехал по старым партиям. */
export async function reissueOzonCargoes(
  shipmentId: number,
): Promise<OzonPlaces> {
  const { data } = await api.post<OzonPlaces>(
    `/supply/ozon/shipments/${shipmentId}/places/reissue`,
  );
  return data;
}

/** Что записано в учёт по поставке. */

/** Записать поставку в 1С: заказ с резервом → паллеты → лист → ПТМО → счёт-фактура.
 *  ⚠️ Необратимо по-разному: заказ помечается на удаление, проведённая передача — сторнируется.
 *  Повторный вызов продолжает с шага, на котором остановились, а не пишет второй комплект. */
export async function createOnecDocuments(
  shipmentId: number,
): Promise<OzonOnec> {
  const { data } = await api.post<OzonOnec>(
    `/supply/ozon/shipments/${shipmentId}/onec`,
  );
  return data;
}

/** Пункт сдачи кросс-дока. У площадки их под сотню — привычный компании идёт первым.
 *  ⚠️ ПВЗ в этом списке нет никогда — их ищут по имени (`searchDropOffs`). */
export interface OzonDropOff {
  id: number;
  name: string;
  /** у списка `/v1/cluster/list` — всегда `CROSS_DOCK` */
  type: string;
}

/** Пункт сдачи, найденный по имени, — любого типа, включая ПВЗ.
 *  `type`: DELIVERY_POINT | ORDERS_RECEIVING_POINT | SORTING_CENTER | CROSS_DOCK | FULL_FILLMENT */
export interface OzonDropOffFound {
  id: number;
  name: string;
  type: string;
  address: string;
}

export async function getOzonDropOffs(): Promise<OzonDropOff[]> {
  const { data } = await api.get<OzonDropOff[]>("/supply/ozon/drop-offs");
  return data;
}

/** Пункты сдачи по имени (`/v1/warehouse/fbo/list`). Каждый вызов идёт в Ozon — звать по кнопке,
 *  не на каждую клавишу. ⚠️ ПВЗ находится только по ПОЛНОМУ имени (`ЛЮБЕРЦЫ_218`): пустой ответ —
 *  не «ПВЗ нет», а «имя не полное или точка исчезла». Короче 4 символов — 422 с подсказкой. */
export async function searchDropOffs(q: string): Promise<OzonDropOffFound[]> {
  const { data } = await api.get<OzonDropOffFound[]>(
    "/supply/ozon/drop-offs/search",
    { params: { q } },
  );
  return data;
}

/** Выбрать тип поставки и пункт сдачи — ПЕРВЫМ шагом, до разговора с площадкой.
 *  ⚠️ Смена типа сбрасывает снятый черновик: числа и окна площадка считала для другого адреса.
 *  `dropOffName` у точки из `/drop-offs` можно не слать; у точки из поиска он ОБЯЗАТЕЛЕН — ровно
 *  `name` из поиска (по нему бэкенд находит точку у Ozon и берёт её тип). */
export async function setOzonSupplyKind(
  shipmentId: number,
  kind: string,
  dropOffId: number | null,
  dropOffName?: string | null,
): Promise<OzonDirection> {
  const { data } = await api.post<OzonDirection>(
    `/supply/ozon/shipments/${shipmentId}/kind`,
    {
      kind,
      drop_off_id: dropOffId,
      ...(dropOffName ? { drop_off_name: dropOffName } : {}),
    },
  );
  return data;
}

/* ────────────────────────── наблюдатель мест ────────────────────────── */

/** Статус мониторинга: на мониторинге | заявка создана | снят | истёк срок. */
export type SlotWatchStatus = "active" | "booked" | "stopped" | "expired";

/** Чем наблюдатель видит окна пункта.
 *  `order` — живая заявка на том же пункте (её окна бесплатны: `timeslot/list` без черновиков);
 *  `probe` — заявок нет, раз в 15 минут пробует черновиком. */
export interface SlotWatchSensor {
  mode: "order" | "probe";
  /** заявка-датчик (при `order`) */
  order_id: string | null;
  /** наша поставка, чья это заявка, и её регион */
  shipment_id: number | null;
  region: string | null;
  /** когда датчик последний раз спрашивали */
  checked_at: string | null;
  /** окон у пункта всего / из них не раньше нужного; null — не спрашивали */
  slots_total: number | null;
  slots_fit: number | null;
  /** начало первого подходящего окна — время площадки, как есть */
  first_fit_from: string | null;
}

/** Что забронировал наблюдатель. */
export interface SlotWatchBooked {
  order_id: string | null;
  supply_number: string | null;
  slot_from: string | null;
  slot_to: string | null;
  /** сколько просили и сколько площадка взяла (берёт меньше — бронируем всё равно) */
  requested: number | null;
  accepted: number | null;
}

/** Мониторинг мест одной поставки (`supply_ozon_slot_watch`). */
export interface SlotWatchView {
  id: number;
  shipment_id: number;
  region: string;
  legal_name: string;
  drop_off_id: number;
  drop_off_name: string;
  drop_off_type: string | null;
  kind: "crossdock";
  /** искать окна не раньше «момент проверки + N часов» */
  not_before_hours: number;
  expires_at: string;
  status: SlotWatchStatus;
  created_by: string;
  created_at: string;
  stopped_by: string | null;
  stopped_at: string | null;
  stop_reason: string | null;
  sensor: SlotWatchSensor | null;
  /** последний тик наблюдателя; null — тиков ещё не было */
  last_tick_at: string | null;
  /** последнее решение словами */
  last_decision: string | null;
  last_error: string | null;
  /** живой черновик наблюдателя и до когда он жив */
  draft_id: string | null;
  draft_alive_until: string | null;
  booked: SlotWatchBooked | null;
}

export type SlotWatchStep =
  | "sensor"
  | "probe"
  | "draft"
  | "timeslots"
  | "order"
  | "resolve"
  | "state"
  | "error";

/** Событие журнала наблюдателя: шаг тика, вызов площадки, ответ и решение словами. */
export interface SlotWatchEventView {
  id: number;
  at: string;
  tick_id: string;
  step: SlotWatchStep;
  ok: boolean;
  http_status: number | null;
  duration_ms: number | null;
  /** метод Ozon (`/v2/supply-order/timeslot/list`); null — шаг без вызова площадки */
  ozon_path: string | null;
  message: string;
  /** запрос и ответ — JSON как есть: объект или массив; null — шаг без вызова площадки */
  request: Record<string, unknown> | unknown[] | null;
  response: Record<string, unknown> | unknown[] | null;
}

export interface SlotWatchStart {
  drop_off_id: number;
  drop_off_name: string;
  drop_off_type: string | null;
  /** 0…624 */
  not_before_hours: number;
  /** 1…14 */
  days: number;
}

/** Мониторинг поставки: активный или последний; null — не ставили ни разу. */
export async function getSlotWatch(
  shipmentId: number,
): Promise<SlotWatchView | null> {
  const { data } = await api.get<SlotWatchView | null>(
    `/supply/ozon/shipments/${shipmentId}/slot-watch`,
  );
  return data;
}

/** Поставить на мониторинг. 409/422 — с причиной по-русски в `detail`. */
export async function startSlotWatch(
  shipmentId: number,
  body: SlotWatchStart,
): Promise<SlotWatchView> {
  const { data } = await api.post<SlotWatchView>(
    `/supply/ozon/shipments/${shipmentId}/slot-watch`,
    body,
  );
  return data;
}

/** Снять с мониторинга. */
export async function stopSlotWatch(shipmentId: number): Promise<SlotWatchView> {
  const { data } = await api.delete<SlotWatchView>(
    `/supply/ozon/shipments/${shipmentId}/slot-watch`,
  );
  return data;
}

/** Журнал мониторинга — новые сверху; `beforeId` — страница ДО этого события. */
export async function getSlotWatchEvents(
  watchId: number,
  limit: number,
  beforeId: number | null,
): Promise<SlotWatchEventView[]> {
  const { data } = await api.get<SlotWatchEventView[]>(
    `/supply/ozon/slot-watch/${watchId}/events`,
    { params: { limit, ...(beforeId != null ? { before_id: beforeId } : {}) } },
  );
  return data;
}

/** Мониторинги по статусу — для отметки в реестре. */
export async function getSlotWatches(
  status: SlotWatchStatus,
): Promise<SlotWatchView[]> {
  const { data } = await api.get<SlotWatchView[]>("/supply/ozon/slot-watches", {
    params: { status },
  });
  return data;
}
